import { NUTRITION_CHART_NO_DATA_MESSAGE } from 'constants/messageConstants';
import {
  NUTRITION_CHART_AREA_HOVER_HINT,
  NUTRITION_CHART_AREA_UNIT,
  NUTRITION_CHART_SEASON_HOVER_HINT,
  NUTRITION_ROW_APPLIED,
  NUTRITION_ROW_BALANCE,
  NUTRITION_ROW_REMOVED,
  NUTRITION_STATUS_BALANCED,
  NUTRITION_STATUS_BUILDING,
  NUTRITION_STATUS_DECLINING,
  NUTRITION_UNIT_KG_PER_HA,
} from 'constants/uiConstants';
import { useElementWidth } from 'hooks/common/useElementWidth';
import React, { useState } from 'react';
import ChartGhostState from '../../FarmDiagnostics/ChartGhostState';
import {
  NUTRIENT_SHORT_LABELS,
  type NutrientKey,
} from '../computeLiveTotals';
import {
  NUTRIENT_REMOVAL_KEY,
  resolveNutrientStatus,
  type NutritionAudit,
  type NutritionSeasonTotals,
  type PaddockNutritionAudit,
} from './computeNutritionAudit';
import NutrientBalanceValue from './NutrientBalanceValue';
import { formatAreaHa, formatKgPerHa } from './nutritionFormat';

// Full class names so Tailwind keeps them. Categorical series in design
// system order (chart-1…chart-6).
const SERIES_STROKE = [
  'stroke-chart-1',
  'stroke-chart-2',
  'stroke-chart-3',
  'stroke-chart-4',
  'stroke-chart-5',
  'stroke-chart-6',
];
const SERIES_BG = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
  'bg-chart-6',
];

// Charts draw at their real on-screen width (useElementWidth), one SVG unit
// to one pixel, so text is the same size however wide the panel is dragged.
const FALLBACK_WIDTH_PX = 360;
const AXIS_TEXT = 'fill-gray-dark text-xs font-semibold';
// Roughly the width a four-digit year label needs, gap included.
const YEAR_LABEL_SPACE_PX = 44;
// Hover target over a year or row — transparent until active, then the
// shared row-hover tint (the same feedback as table rows and the tree).
const HOVER_TARGET = 'cursor-pointer outline-none';
const hoverFill = (active: boolean) =>
  active ? 'fill-rowHover' : 'fill-transparent';

/**
 * Whether the year at index i gets an axis label. Labels are thinned to fit
 * the width, counting back from the latest year so it is always labelled.
 */
const showYearLabel = (i: number, count: number, plotWidth: number) => {
  const maxLabels = Math.max(1, Math.floor(plotWidth / YEAR_LABEL_SPACE_PX));
  const step = Math.ceil(count / maxLabels);
  return (count - 1 - i) % step === 0;
};

const LegendItem: React.FC<{ swatch: string; label: string }> = ({
  swatch,
  label,
}) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
    <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
    {label}
  </span>
);

/**
 * The figures for whatever is hovered or focused, under the chart. A fixed
 * minimum height so the card doesn't jump as the readout appears and goes.
 */
const Readout: React.FC<{ hint: string; children?: React.ReactNode }> = ({
  hint,
  children,
}) => (
  <div
    aria-live="polite"
    className="mt-2 flex min-h-[2rem] flex-wrap items-center gap-x-4 gap-y-1 text-base"
  >
    {children ?? <span className="text-sm text-gray-muted">{hint}</span>}
  </div>
);

/** Handlers that make an SVG element hoverable and keyboard-focusable. */
const hoverProps = (
  key: number,
  setActive: (key: number | null) => void
) => ({
  tabIndex: 0,
  onMouseEnter: () => setActive(key),
  onMouseLeave: () => setActive(null),
  onFocus: () => setActive(key),
  onBlur: () => setActive(null),
});

/* ═══════════════════════════════════════════════════════════════
   Applied vs removed by year — one small chart per nutrient
   ═══════════════════════════════════════════════════════════════ */

const SEASON_CHART_HEIGHT = 120;
const SEASON_PAD = { top: 8, right: 4, bottom: 22, left: 48 };

const SeasonBarsPanel: React.FC<{
  nutrient: NutrientKey;
  totals: NutritionSeasonTotals[];
  width: number;
}> = ({ nutrient, totals, width }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const max = Math.max(
    0,
    ...totals.flatMap((t) => [t.applied[nutrient], t.removed[nutrient]])
  );
  const hasRemovalKey = NUTRIENT_REMOVAL_KEY[nutrient] != null;
  const plotWidth = width - SEASON_PAD.left - SEASON_PAD.right;
  const plotHeight = SEASON_CHART_HEIGHT - SEASON_PAD.top - SEASON_PAD.bottom;
  const baseline = SEASON_PAD.top + plotHeight;
  const slot = plotWidth / Math.max(totals.length, 1);
  const barWidth = Math.max(1, Math.min(18, (slot - 4) / 2));
  const y = (value: number) =>
    baseline - (max > 0 ? (value / max) * plotHeight : 0);
  const active = activeIndex != null ? totals[activeIndex] : null;

  return (
    <figure className="flex flex-col">
      <figcaption className="text-sm font-bold text-charcoal">
        {`${NUTRIENT_SHORT_LABELS[nutrient]} `}
        <span className="font-normal text-gray-muted">
          {NUTRITION_UNIT_KG_PER_HA}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${width} ${SEASON_CHART_HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`${NUTRIENT_SHORT_LABELS[nutrient]} ${NUTRITION_ROW_APPLIED} / ${NUTRITION_ROW_REMOVED}, ${NUTRITION_UNIT_KG_PER_HA}`}
      >
        <text
          x={SEASON_PAD.left - 6}
          y={SEASON_PAD.top + 8}
          textAnchor="end"
          className={AXIS_TEXT}
        >
          {formatKgPerHa(max)}
        </text>
        <text
          x={SEASON_PAD.left - 6}
          y={baseline}
          textAnchor="end"
          className={AXIS_TEXT}
        >
          0
        </text>
        {totals.map((t, i) => {
          const left = SEASON_PAD.left + slot * i;
          const centre = left + slot / 2;
          const applied = t.applied[nutrient];
          const removed = t.removed[nutrient];
          return (
            <g
              key={t.season}
              className={HOVER_TARGET}
              aria-label={`${t.season}: ${NUTRITION_ROW_APPLIED} ${formatKgPerHa(applied)}, ${NUTRITION_ROW_REMOVED} ${formatKgPerHa(removed)} ${NUTRITION_UNIT_KG_PER_HA}`}
              {...hoverProps(i, setActiveIndex)}
            >
              <rect
                x={left}
                y={SEASON_PAD.top}
                width={slot}
                height={plotHeight}
                className={hoverFill(activeIndex === i)}
              />
              <rect
                x={hasRemovalKey ? centre - barWidth - 1 : centre - barWidth / 2}
                y={y(applied)}
                width={barWidth}
                height={baseline - y(applied)}
                className="fill-chart-1"
              />
              {hasRemovalKey && (
                <rect
                  x={centre + 1}
                  y={y(removed)}
                  width={barWidth}
                  height={baseline - y(removed)}
                  className="fill-chart-2"
                />
              )}
              {showYearLabel(i, totals.length, plotWidth) && (
                <text
                  x={centre}
                  y={SEASON_CHART_HEIGHT - 6}
                  textAnchor="middle"
                  className={AXIS_TEXT}
                >
                  {t.season}
                </text>
              )}
            </g>
          );
        })}
        <line
          x1={SEASON_PAD.left}
          x2={width - SEASON_PAD.right}
          y1={baseline}
          y2={baseline}
          className="stroke-gray-medium"
        />
      </svg>
      <Readout hint={NUTRITION_CHART_SEASON_HOVER_HINT}>
        {active && (
          <>
            <span className="font-bold text-charcoal">{active.season}</span>
            <span className="text-charcoal">
              {`${NUTRITION_ROW_APPLIED} ${formatKgPerHa(active.applied[nutrient])}`}
            </span>
            {hasRemovalKey && (
              <span className="text-charcoal">
                {`${NUTRITION_ROW_REMOVED} ${formatKgPerHa(active.removed[nutrient])}`}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-charcoal">
              {NUTRITION_ROW_BALANCE}
              <NutrientBalanceValue
                nutrient={nutrient}
                applied={active.applied[nutrient]}
                removed={active.removed[nutrient]}
                balance={active.applied[nutrient] - active.removed[nutrient]}
              />
            </span>
          </>
        )}
      </Readout>
    </figure>
  );
};

/**
 * Applied vs removed (kg/ha) for each year in range, one small chart per
 * nutrient so each keeps its own scale (N is usually ten times K). At farm
 * or org scope each year is weighted by planted area (computeSeasonTotals).
 * Hover or focus a year for its figures.
 */
export const AppliedRemovedBySeasonChart: React.FC<{
  totals: NutritionSeasonTotals[];
  nutrients: NutrientKey[];
}> = ({ totals, nutrients }) => {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isEmpty = totals.every((t) =>
    nutrients.every((key) => t.applied[key] <= 0 && t.removed[key] <= 0)
  );

  return (
    <ChartGhostState active={isEmpty} message={NUTRITION_CHART_NO_DATA_MESSAGE}>
      <div className="mb-3 flex flex-wrap gap-3">
        <LegendItem swatch="bg-chart-1" label={NUTRITION_ROW_APPLIED} />
        <LegendItem swatch="bg-chart-2" label={NUTRITION_ROW_REMOVED} />
      </div>
      <div ref={ref} className="flex flex-col gap-4">
        {nutrients.map((key) => (
          <SeasonBarsPanel
            key={key}
            nutrient={key}
            totals={totals}
            width={width || FALLBACK_WIDTH_PX}
          />
        ))}
      </div>
    </ChartGhostState>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Single paddock — running balance across years
   ═══════════════════════════════════════════════════════════════ */

const LINE_HEIGHT = 180;
const LINE_PAD = { top: 12, right: 12, bottom: 24, left: 56 };

export const RunningBalanceChart: React.FC<{
  paddock: PaddockNutritionAudit | null;
  nutrients: NutrientKey[];
}> = ({ paddock, nutrients }) => {
  const { ref, width: measured } = useElementWidth<HTMLDivElement>();
  const width = measured || FALLBACK_WIDTH_PX;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Only nutrients whose balance can be judged, capped at the six series colours.
  const series = nutrients
    .filter((key) => NUTRIENT_REMOVAL_KEY[key] != null)
    .slice(0, SERIES_STROKE.length);
  const seasons = paddock?.seasons ?? [];

  const running = series.map((key) => {
    let total = 0;
    return seasons.map((season) => {
      total += season.balance[key];
      return total;
    });
  });
  const values = running.flat();
  const isEmpty =
    seasons.length === 0 ||
    seasons.every((s) =>
      series.every((key) => s.applied[key] <= 0 && s.removed[key] <= 0)
    );

  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const plotWidth = width - LINE_PAD.left - LINE_PAD.right;
  const plotHeight = LINE_HEIGHT - LINE_PAD.top - LINE_PAD.bottom;
  const slot = plotWidth / Math.max(seasons.length, 1);
  const x = (i: number) =>
    LINE_PAD.left +
    (seasons.length <= 1 ? plotWidth / 2 : (i / (seasons.length - 1)) * plotWidth);
  const y = (value: number) =>
    LINE_PAD.top + ((max - value) / span) * plotHeight;

  return (
    <ChartGhostState active={isEmpty} message={NUTRITION_CHART_NO_DATA_MESSAGE}>
      <div className="mb-2 flex flex-wrap gap-3">
        {series.map((key, s) => (
          <LegendItem
            key={key}
            swatch={SERIES_BG[s]}
            label={NUTRIENT_SHORT_LABELS[key]}
          />
        ))}
        <span className="text-sm text-gray-muted">
          {NUTRITION_UNIT_KG_PER_HA}
        </span>
      </div>
      <div ref={ref}>
        <svg
          viewBox={`0 0 ${width} ${LINE_HEIGHT}`}
          className="w-full"
          role="img"
          aria-label={`${series.map((key) => NUTRIENT_SHORT_LABELS[key]).join(', ')}, ${NUTRITION_UNIT_KG_PER_HA}`}
        >
          {/* Hover columns sit under the lines so the points stay visible. */}
          {seasons.map((season, i) => (
            <rect
              key={`hover-${season.season}`}
              x={x(i) - slot / 2}
              y={LINE_PAD.top}
              width={slot}
              height={plotHeight}
              className={`${HOVER_TARGET} ${hoverFill(activeIndex === i)}`}
              aria-label={String(season.season)}
              {...hoverProps(i, setActiveIndex)}
            />
          ))}
          <text x={LINE_PAD.left - 6} y={y(max) + 4} textAnchor="end" className={AXIS_TEXT}>
            {formatKgPerHa(max)}
          </text>
          <text x={LINE_PAD.left - 6} y={y(min) + 4} textAnchor="end" className={AXIS_TEXT}>
            {formatKgPerHa(min)}
          </text>
          {/* Zero line — dashed gray-dark, an annotation rather than a series. */}
          <line
            x1={LINE_PAD.left}
            x2={width - LINE_PAD.right}
            y1={y(0)}
            y2={y(0)}
            strokeDasharray="4 3"
            className="pointer-events-none stroke-gray-dark"
          />
          {seasons.map(
            (season, i) =>
              showYearLabel(i, seasons.length, plotWidth) && (
                <text
                  key={season.season}
                  x={x(i)}
                  y={LINE_HEIGHT - 6}
                  textAnchor="middle"
                  className={AXIS_TEXT}
                >
                  {season.season}
                </text>
              )
          )}
          {series.map((key, s) => (
            <g key={key} className="pointer-events-none">
              <polyline
                points={running[s].map((v, i) => `${x(i)},${y(v)}`).join(' ')}
                fill="none"
                strokeWidth={2}
                className={SERIES_STROKE[s]}
              />
              {running[s].map((v, i) => (
                <circle
                  key={seasons[i].season}
                  cx={x(i)}
                  cy={y(v)}
                  r={activeIndex === i ? 4.5 : 3}
                  strokeWidth={2}
                  className={`fill-white ${SERIES_STROKE[s]}`}
                />
              ))}
            </g>
          ))}
        </svg>
      </div>
      <Readout hint={NUTRITION_CHART_SEASON_HOVER_HINT}>
        {activeIndex != null && (
          <>
            <span className="font-bold text-charcoal">
              {seasons[activeIndex].season}
            </span>
            {series.map((key, s) => (
              <span key={key} className="inline-flex items-center gap-1.5">
                <span className={`inline-block h-2.5 w-2.5 rounded-sm ${SERIES_BG[s]}`} />
                {NUTRIENT_SHORT_LABELS[key]}
                <NutrientBalanceValue
                  nutrient={key}
                  applied={Math.max(running[s][activeIndex], 0)}
                  removed={Math.max(-running[s][activeIndex], 0)}
                  balance={running[s][activeIndex]}
                />
              </span>
            ))}
          </>
        )}
      </Readout>
    </ChartGhostState>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Farm / org — area building vs declining per nutrient
   ═══════════════════════════════════════════════════════════════ */

type AreaStatus = 'building' | 'balanced' | 'declining';

const AREA_STATUS_ORDER: AreaStatus[] = ['building', 'balanced', 'declining'];
const AREA_STATUS_FILL: Record<AreaStatus, string> = {
  building: 'fill-success',
  balanced: 'fill-gray-medium',
  declining: 'fill-danger-dark',
};
const AREA_STATUS_BG: Record<AreaStatus, string> = {
  building: 'bg-success',
  balanced: 'bg-gray-medium',
  declining: 'bg-danger-dark',
};
const AREA_STATUS_LABEL: Record<AreaStatus, string> = {
  building: NUTRITION_STATUS_BUILDING,
  balanced: NUTRITION_STATUS_BALANCED,
  declining: NUTRITION_STATUS_DECLINING,
};

const BAR_LABEL_WIDTH = 32;
const BAR_VALUE_WIDTH = 72;
const STACK_ROW_HEIGHT = 32;
const STACK_BAR_HEIGHT = 16;

export const AreaByStatusChart: React.FC<{
  audit: NutritionAudit;
  nutrients: NutrientKey[];
}> = ({ audit, nutrients }) => {
  const { ref, width: measured } = useElementWidth<HTMLDivElement>();
  const width = measured || FALLBACK_WIDTH_PX;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const series = nutrients.filter((key) => NUTRIENT_REMOVAL_KEY[key] != null);
  const paddocks = audit.farms.flatMap((farm) => farm.paddocks);

  const rows = series.map((key) => {
    const area: Record<AreaStatus, number> = {
      building: 0,
      balanced: 0,
      declining: 0,
    };
    for (const paddock of paddocks) {
      const status = resolveNutrientStatus(
        key,
        paddock.applied[key],
        paddock.removed[key],
        paddock.balance[key]
      );
      if (status === 'building' || status === 'balanced' || status === 'declining') {
        area[status] += paddock.areaHa;
      }
    }
    return { key, area };
  });

  const maxArea = Math.max(
    0,
    ...rows.map((r) => r.area.building + r.area.balanced + r.area.declining)
  );
  const isEmpty = maxArea <= 0;
  const plotWidth = width - BAR_LABEL_WIDTH - BAR_VALUE_WIDTH;
  const scale = (value: number) => (isEmpty ? 0 : (value / maxArea) * plotWidth);
  const active = activeIndex != null ? rows[activeIndex] : null;

  return (
    <ChartGhostState active={isEmpty} message={NUTRITION_CHART_NO_DATA_MESSAGE}>
      <div className="mb-2 flex flex-wrap gap-3">
        {AREA_STATUS_ORDER.map((status) => (
          <LegendItem
            key={status}
            swatch={AREA_STATUS_BG[status]}
            label={AREA_STATUS_LABEL[status]}
          />
        ))}
      </div>
      <div ref={ref}>
        <svg
          viewBox={`0 0 ${width} ${Math.max(rows.length, 1) * STACK_ROW_HEIGHT}`}
          className="w-full"
          role="img"
          aria-label={`${NUTRITION_STATUS_BUILDING} / ${NUTRITION_STATUS_BALANCED} / ${NUTRITION_STATUS_DECLINING}, ${NUTRITION_CHART_AREA_UNIT}`}
        >
          {rows.map(({ key, area }, i) => {
            const y = i * STACK_ROW_HEIGHT + 8;
            let offset = BAR_LABEL_WIDTH;
            return (
              <g
                key={key}
                className={HOVER_TARGET}
                aria-label={`${NUTRIENT_SHORT_LABELS[key]}: ${AREA_STATUS_ORDER.map((status) => `${AREA_STATUS_LABEL[status]} ${formatAreaHa(area[status])} ${NUTRITION_CHART_AREA_UNIT}`).join(', ')}`}
                {...hoverProps(i, setActiveIndex)}
              >
                <rect
                  x={0}
                  y={i * STACK_ROW_HEIGHT}
                  width={width}
                  height={STACK_ROW_HEIGHT}
                  className={hoverFill(activeIndex === i)}
                />
                <text
                  x={4}
                  y={y + STACK_BAR_HEIGHT - 3}
                  className="fill-charcoal text-sm font-bold"
                >
                  {NUTRIENT_SHORT_LABELS[key]}
                </text>
                {AREA_STATUS_ORDER.map((status) => {
                  const barWidth = scale(area[status]);
                  const rect = (
                    <rect
                      key={status}
                      x={offset}
                      y={y}
                      width={barWidth}
                      height={STACK_BAR_HEIGHT}
                      className={AREA_STATUS_FILL[status]}
                    />
                  );
                  offset += barWidth;
                  return rect;
                })}
                <text
                  x={offset + 6}
                  y={y + STACK_BAR_HEIGHT - 3}
                  className={AXIS_TEXT}
                >
                  {`${formatAreaHa(area.declining)} ${NUTRITION_CHART_AREA_UNIT} ▼`}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <Readout hint={NUTRITION_CHART_AREA_HOVER_HINT}>
        {active && (
          <>
            <span className="font-bold text-charcoal">
              {NUTRIENT_SHORT_LABELS[active.key]}
            </span>
            {AREA_STATUS_ORDER.map((status) => (
              <span key={status} className="inline-flex items-center gap-1.5 text-charcoal">
                <span className={`inline-block h-2.5 w-2.5 rounded-sm ${AREA_STATUS_BG[status]}`} />
                {`${AREA_STATUS_LABEL[status]} ${formatAreaHa(active.area[status])} ${NUTRITION_CHART_AREA_UNIT}`}
              </span>
            ))}
          </>
        )}
      </Readout>
    </ChartGhostState>
  );
};
